import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, Send, Loader2, CheckCircle, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ContractWiserLogo } from "@/components/ContractWiserLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  useEffect(() => {
    document.title = "Contact Us | ContractWiser";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact/submit`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try emailing us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <ContractWiserLogo size="default" />
          </button>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-eucalypt-700">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-eucalypt-800 mb-3">
                Contact Us
              </h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Have a question about our service? Need help with your contract analysis? We're here to help.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Contact Info Cards */}
              <div className="space-y-6">
                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-eucalypt-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-eucalypt-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-eucalypt-800 mb-1">Email Us</h3>
                        <a 
                          href="mailto:admin@contractwiser.com.au" 
                          className="text-terracotta-600 hover:underline"
                        >
                          admin@contractwiser.com.au
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-eucalypt-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-eucalypt-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-eucalypt-800 mb-1">Response Time</h3>
                        <p className="text-muted-foreground text-sm">
                          We typically respond within 24-48 business hours
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-eucalypt-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-eucalypt-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-eucalypt-800 mb-1">Location</h3>
                        <p className="text-muted-foreground text-sm">
                          Australia-based service<br />
                          Supporting all Australian states and territories
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <div className="md:col-span-2">
                <Card className="border-0 shadow-float">
                  <CardHeader className="bg-eucalypt-800 text-white px-8 py-6">
                    <CardTitle className="font-heading flex items-center gap-3">
                      <Send className="h-5 w-5" />
                      Send Us a Message
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8">
                    {submitted ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-12"
                      >
                        <div className="w-16 h-16 bg-eucalypt-100 flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="h-8 w-8 text-eucalypt-700" />
                        </div>
                        <h3 className="font-heading text-xl text-eucalypt-800 mb-2">Message Sent!</h3>
                        <p className="text-muted-foreground mb-6">
                          Thank you for contacting us. We'll get back to you within 24-48 hours.
                        </p>
                        <Button
                          onClick={() => {
                            setSubmitted(false);
                            setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                          }}
                          variant="outline"
                          className="border-eucalypt-300 text-eucalypt-700"
                        >
                          Send Another Message
                        </Button>
                      </motion.div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="name" className="text-eucalypt-800 font-semibold mb-2 block">
                              Your Name *
                            </Label>
                            <Input
                              id="name"
                              name="name"
                              data-testid="contact-name"
                              placeholder="John Smith"
                              value={formData.name}
                              onChange={handleChange}
                              className="border-sandstone-300 focus:border-eucalypt-500"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="email" className="text-eucalypt-800 font-semibold mb-2 block">
                              Email Address *
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              data-testid="contact-email"
                              placeholder="john@example.com"
                              value={formData.email}
                              onChange={handleChange}
                              className="border-sandstone-300 focus:border-eucalypt-500"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="phone" className="text-eucalypt-800 font-semibold mb-2 block">
                              Phone Number (Optional)
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              data-testid="contact-phone"
                              placeholder="0400 000 000"
                              value={formData.phone}
                              onChange={handleChange}
                              className="border-sandstone-300 focus:border-eucalypt-500"
                            />
                          </div>
                          <div>
                            <Label htmlFor="subject" className="text-eucalypt-800 font-semibold mb-2 block">
                              Subject (Optional)
                            </Label>
                            <Input
                              id="subject"
                              name="subject"
                              data-testid="contact-subject"
                              placeholder="Question about my report"
                              value={formData.subject}
                              onChange={handleChange}
                              className="border-sandstone-300 focus:border-eucalypt-500"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="message" className="text-eucalypt-800 font-semibold mb-2 block">
                            Your Message *
                          </Label>
                          <Textarea
                            id="message"
                            name="message"
                            data-testid="contact-message"
                            placeholder="How can we help you?"
                            value={formData.message}
                            onChange={handleChange}
                            className="border-sandstone-300 focus:border-eucalypt-500 min-h-[150px]"
                            required
                          />
                        </div>

                        <Button
                          type="submit"
                          data-testid="contact-submit"
                          disabled={isSubmitting}
                          className="w-full bg-terracotta-600 hover:bg-terracotta-700 text-white py-6 text-lg"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="h-5 w-5 mr-2" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
